import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { apiUrl } from "../../utils/apiUrl";
import { addReportToServer } from "../../functions/sendReport";
import { getPointsPerMatch, formatMmSs } from "../../utils/talentPoints";
import { isUploadOpen } from "../../utils/talentTheme";
import { saveTalentSession, appendTalentAnswer } from "../../utils/talentSession";
import {
  fetchTalentGameStatus,
  getTalentResumePath,
} from "../../utils/talentResumeRoute";
import TalentHud from "../../components/talent/TalentHud";
import TalentThemedModal from "../../components/talent/TalentThemedModal";
import useTalentTouchDrag from "../../hooks/useTalentTouchDrag";
import "./talentPoses.css";

const DEMO_POSE_LIMIT = 3;

function appBaseUrl() {
  const b = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
  return b || (typeof window !== "undefined" ? window.location.origin : "");
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

function isDemoUser(user) {
  return (
    user?.source === "DEMO" ||
    user?.role === "DEMO" ||
    user?.role === "demobypass"
  );
}

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function normalizePoses(raw) {
  if (!Array.isArray(raw)) return [];
  const seenIds = new Set();
  const seenImages = new Set();
  const out = [];

  raw.forEach((p, i) => {
    const name = String(p?.name || "").trim();
    const image = String(p?.image || "").trim();
    if (!name || !image) return;

    let id = Number(p?.id);
    if (!Number.isFinite(id) || id <= 0 || seenIds.has(id)) {
      id = i + 1;
      while (seenIds.has(id)) id += 1;
    }
    seenIds.add(id);

    const imageKey = image.toLowerCase();
    if (seenImages.has(imageKey)) return;
    seenImages.add(imageKey);

    out.push({ id, name, image });
  });

  return out;
}

export default function TalentPoses() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const [batchSize, setBatchSize] = useState(4);
  const allPosesRaw = useMemo(() => normalizePoses(themeData?.yogaPoses), [themeData]);
  const isDemo = useMemo(() => isDemoUser(user), [user]);
  const allPoses = useMemo(
    () => (isDemo ? allPosesRaw.slice(0, DEMO_POSE_LIMIT) : allPosesRaw),
    [allPosesRaw, isDemo]
  );
  const pointsPerMatch = useMemo(() => getPointsPerMatch(themeData), [themeData]);

  const [batchIndex, setBatchIndex] = useState(0);
  const [globalMatches, setGlobalMatches] = useState({});
  const [score, setScore] = useState(0);
  const [timerSec, setTimerSec] = useState(0);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [stageComplete, setStageComplete] = useState(null);
  const [trayOrder, setTrayOrder] = useState([]);
  const [dragName, setDragName] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [shakePoseId, setShakePoseId] = useState(null);
  const submittingRef = useRef(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const advancedForBatchRef = useRef(-1);
  const scoreRef = useRef(0);
  const timerRef = useRef(0);
  const userRef = useRef(user);
  const reportIdRef = useRef(null);
  const globalMatchesRef = useRef(globalMatches);
  const demoLimitHandledRef = useRef(false);

  const batchStart = batchIndex * batchSize;
  const currentBatch = useMemo(
    () => allPoses.slice(batchStart, batchStart + batchSize),
    [allPoses, batchStart, batchSize]
  );

  const pageTitle =
    themeData?.yoga_poses_page_title ||
    "Can you match each yoga pose with its correct name?";

  const batchKey = useMemo(
    () => currentBatch.map((p) => p.id).join(","),
    [currentBatch]
  );

  const usedNamesInBatch = useMemo(() => {
    const names = new Set();
    currentBatch.forEach((p) => {
      if (globalMatches[p.id] === p.name) names.add(p.name);
    });
    return names;
  }, [currentBatch, globalMatches]);

  const batchComplete =
    currentBatch.length > 0 &&
    currentBatch.every((p) => globalMatches[p.id] === p.name);

  const allComplete =
    allPoses.length > 0 &&
    allPoses.every((p) => globalMatches[p.id] === p.name);

  const progressCount = Object.keys(globalMatches).length;

  const hasMoreBatches = batchStart + batchSize < allPoses.length;

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    globalMatchesRef.current = globalMatches;
  }, [globalMatches]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mqMobile = window.matchMedia("(max-width: 768px)");
    const sync = () => setBatchSize(mqMobile.matches ? 3 : 4);
    sync();
    mqMobile.addEventListener("change", sync);
    return () => mqMobile.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!batchKey) return;
    setTrayOrder(shuffleArray(currentBatch.map((p) => p.name)));
    setDragName("");
    setSelectedName("");
    setShakePoseId(null);
  }, [batchIndex, batchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!batchComplete || !hasMoreBatches) return undefined;
    if (advancedForBatchRef.current === batchIndex) return undefined;

    advancedForBatchRef.current = batchIndex;
    const t = window.setTimeout(() => {
      setBatchIndex((b) => b + 1);
    }, 450);

    return () => window.clearTimeout(t);
  }, [batchComplete, hasMoreBatches, batchIndex]);

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || "/login?&save=true"));
  }, [dispatch, user?.backButtonRedirect]);

  // The resume check needs to run once per visit. Depending on the whole user/themeData objects
  // re-fired it (and the API call) whenever either object was replaced, e.g. on a theme retry.
  const resumeCheckedRef = useRef(false);
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);
  useEffect(() => {
    if (isDemo || !user || !adminToken || !themeData || status !== "succeeded") return undefined;
    if (resumeCheckedRef.current) return undefined;
    resumeCheckedRef.current = true;

    (async () => {
      try {
        const data = await fetchTalentGameStatus(adminToken, user);
        if (unmountedRef.current) return;
        const path = getTalentResumePath(data, themeData);
        if (path !== "/yoga-poses") {
          navigate(path, { replace: true });
        }
      } catch (e) {
        console.error("yoga resume route on poses:", e);
      }
    })();

    return undefined;
  }, [isDemo, user, adminToken, themeData, status, navigate]);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    timerRef.current = timerSec;
  }, [timerSec]);

  useEffect(() => {
    // Stop the clock once every pose is matched so the submitted time matches what is shown.
    if (allComplete) return undefined;
    const id = window.setInterval(() => {
      setTimerSec((t) => t + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [allComplete]);

  const redirectDemoToPlans = useCallback(() => {
    if (demoLimitHandledRef.current) return;
    demoLimitHandledRef.current = true;
    sessionStorage.setItem("spotThankYouMsg", "Thanks for playing (demo).");
    window.setTimeout(() => {
      Swal.fire({
        icon: "success",
        title: "Thank you for playing.",
        text: "Subscribe to any PLAN to play with your peers.",
      }).then(() => {
        window.location.href = demoPlansUrl();
      });
    }, 0);
  }, []);

  const pushMatchReport = useCallback((nextScore, matchedNames) => {
    if (isDemoUser(userRef.current)) return;
    saveTalentSession({
      score: nextScore,
      timeSec: timerRef.current,
      matchedNames,
    });
    addReportToServer(userRef.current, {
      points: nextScore,
      elapsedSeconds: timerRef.current,
      answers: matchedNames,
      reportIdRef,
    });
  }, []);

  const tryAssign = useCallback(
    (pose, labelName) => {
      if (demoLimitHandledRef.current) return false;

      const name = String(labelName || "").trim();
      if (!name || usedNamesInBatch.has(name)) return false;
      if (globalMatches[pose.id]) return false;

      if (name !== pose.name) {
        setShakePoseId(pose.id);
        window.setTimeout(() => setShakePoseId(null), 400);
        return false;
      }

      const nextMatches = { ...globalMatches, [pose.id]: name };
      const matchedCount = Object.keys(nextMatches).length;

      setGlobalMatches(nextMatches);
      const nextScore = scoreRef.current + pointsPerMatch;
      scoreRef.current = nextScore;
      setScore(nextScore);
      const matchedNames = appendTalentAnswer(name);
      pushMatchReport(nextScore, matchedNames);
      setSelectedName("");
      setDragName("");

      if (isDemoUser(userRef.current) && matchedCount >= DEMO_POSE_LIMIT) {
        redirectDemoToPlans();
      }

      return true;
    },
    [usedNamesInBatch, globalMatches, pointsPerMatch, pushMatchReport, redirectDemoToPlans]
  );

  const handleTouchDrop = useCallback(
    (poseId, name) => {
      const pose = currentBatch.find((p) => p.id === poseId);
      if (pose) tryAssign(pose, name);
    },
    [currentBatch, tryAssign]
  );

  const { allowHtml5Drag, ghost, hoverPoseId, startPointerPick } = useTalentTouchDrag({
    onDrop: handleTouchDrop,
    enabled: true,
  });

  const handleDrop = (e, pose) => {
    e.preventDefault();
    const name = e.dataTransfer.getData("text/plain") || dragName;
    tryAssign(pose, name);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleTrayDragStart = (e, name) => {
    if (!allowHtml5Drag || usedNamesInBatch.has(name)) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData("text/plain", name);
    e.dataTransfer.effectAllowed = "move";
    setDragName(name);
  };

  const handleTrayClick = (name) => {
    if (usedNamesInBatch.has(name)) return;
    setSelectedName((prev) => (prev === name ? "" : name));
  };

  const handleDropZoneClick = (pose) => {
    if (!selectedName || globalMatches[pose.id]) return;
    tryAssign(pose, selectedName);
  };

  const finalizeGame = useCallback(async () => {
    if (isDemoUser(user)) return;
    if (submittingRef.current || !user || !adminToken) return;
    submittingRef.current = true;

    const matchedNames = allPoses
      .filter((p) => globalMatchesRef.current[p.id] === p.name)
      .map((p) => p.name);
    saveTalentSession({
      score: scoreRef.current,
      timeSec: timerRef.current,
      matchedNames,
    });

    try {
      const resp = await fetch(apiUrl("/spotSubmit"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user.userId,
          email: user.email || "",
          name: user.name || "",
          time: formatMmSs(timerRef.current),
          spot_score: allPoses.length,
          total_score: allPoses.length,
          points: scoreRef.current,
          currentTheme: themeData?.themename || themeData?.themeName,
        }),
      });

      const text = await resp.text();
      let payload = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {
        payload = null;
      }

      if (!resp.ok) {
        await Swal.fire({
          icon: "error",
          title: "Submit failed",
          text: payload?.error || text || "Could not save score.",
        });
        submittingRef.current = false;
        setSubmitFailed(true);
        return;
      }
      setSubmitFailed(false);

      const uploadOpen = isUploadOpen(themeData);
      setStageComplete({
        text: uploadOpen
          ? "You've matched all the yoga poses — great work! Next, upload a photo or video of yourself in a yoga pose."
          : "You've matched all the yoga poses — great work! Next, explore the yoga gallery.",
        nextPath: uploadOpen ? "/yoga-upload" : "/yoga-gallery",
      });
    } catch (e) {
      console.error("spotSubmit", e);
      await Swal.fire({ icon: "error", title: "Network error", text: String(e.message) });
      submittingRef.current = false;
      setSubmitFailed(true);
    }
  }, [user, adminToken, allPoses, themeData, navigate]);

  useEffect(() => {
    if (isDemo || !allComplete || submittingRef.current) return undefined;

    const t = window.setTimeout(() => {
      finalizeGame();
    }, 500);

    return () => window.clearTimeout(t);
  }, [isDemo, allComplete, finalizeGame]);

  const titleColor = themeData?.text_color || "#1a2b6d";
  const rules = Array.isArray(themeData?.rules) ? themeData.rules : [];

  const handleStageCompleteConfirm = useCallback(() => {
    if (!stageComplete?.nextPath) return;
    const nextPath = stageComplete.nextPath;
    setStageComplete(null);
    navigate(nextPath, { replace: true });
  }, [stageComplete, navigate]);

  if (status === "loading" || status === "idle") {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="yp-page">
        <p style={{ textAlign: "center", color: "#1a2b6d" }}>
          Could not load game data. Make sure the backend is running on port 5001.
          {themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="yp-submit"
          style={{ maxWidth: 200, margin: "16px auto", display: "block" }}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!allPoses.length) {
    return (
      <div className="yp-page">
        <p style={{ textAlign: "center", color: titleColor }}>
          No yoga poses configured for this theme yet.
        </p>
      </div>
    );
  }

  return (
    <div className="yp-page">
      <h1 className="yp-title" style={{ color: titleColor }}>
        {pageTitle}
      </h1>

      <div className="yp-game-panel">
        <TalentHud
          score={score}
          timeSec={timerSec}
          progressCount={progressCount}
          progressTotal={allPoses.length}
          showHowToPlay
          onHowToPlay={() => setRulesOpen(true)}
          inPanel
        />

        {submitFailed && allComplete ? (
          <button
            type="button"
            className="yp-submit"
            style={{ maxWidth: 260, margin: "12px auto", display: "block" }}
            onClick={() => {
              setSubmitFailed(false);
              finalizeGame();
            }}
          >
            Retry saving my score
          </button>
        ) : null}

        <div className="yp-layout">
          <div className="yp-pose-grid yp-pose-grid--enter" key={batchIndex}>
            {currentBatch.map((pose, idx) => {
              const filled = globalMatches[pose.id];
              const isFilled = filled === pose.name;
              return (
                <div className="yp-pose-card" key={pose.id}>
                  <div className="yp-pose-top">
                    <span className="yp-pose-num">{batchStart + idx + 1}.</span>
                    <div className="yp-pose-img-wrap">
                      <img
                        src={s3ThemeAssetUrl(pose.image)}
                        alt={pose.name}
                        draggable={false}
                      />
                    </div>
                  </div>
                  <div
                    className={`yp-drop-zone${isFilled ? " yp-drop-zone--filled" : ""}${
                      shakePoseId === pose.id ? " yp-shake yp-drop-zone--wrong" : ""
                    }${hoverPoseId === pose.id && shakePoseId !== pose.id ? " yp-drop-zone--hover" : ""}`}
                    data-pose-id={pose.id}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, pose)}
                    onClick={() => handleDropZoneClick(pose)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") handleDropZoneClick(pose);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Drop zone for pose ${pose.name}`}
                  >
                    {isFilled ? (
                      <span className="yp-drop-label" title={filled}>
                        {filled}
                      </span>
                    ) : (
                      <span className="yp-drop-placeholder">Drop name here</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="yp-tray">
            {trayOrder.map((name) => {
              const used = usedNamesInBatch.has(name);
              return (
                <div
                  key={`${batchIndex}-${name}`}
                  className={`yp-chip${used ? " yp-chip--used" : ""}${
                    selectedName === name ? " yp-chip--selected" : ""
                  }`}
                  draggable={allowHtml5Drag && !used}
                  onDragStart={(e) => handleTrayDragStart(e, name)}
                  onDragEnd={() => setDragName("")}
                  onPointerDown={(e) => {
                    if (!used) startPointerPick(e, name);
                  }}
                  onClick={() => handleTrayClick(name)}
                >
                  <span title={name}>{name}</span>
                  <span className="yp-chip-grip" aria-hidden>
                    ≡
                  </span>
                </div>
              );
            })}
          </aside>
        </div>
      </div>

      {ghost.visible ? (
        <div
          className="yp-drag-ghost"
          style={{ left: ghost.x, top: ghost.y }}
          aria-hidden
        >
          {ghost.label}
        </div>
      ) : null}

      <TalentThemedModal
        open={rulesOpen}
        title="How to Play"
        titleId="yp-rules-title"
        themeData={themeData}
        confirmText="Got it"
        onConfirm={() => setRulesOpen(false)}
      >
        <ul>
          {rules.length > 0 ? (
            rules.map((rule, i) => <li key={i}>{rule}</li>)
          ) : (
            <li>
              Drag each pose name to the matching image. Complete all poses to continue.
            </li>
          )}
        </ul>
      </TalentThemedModal>

      <TalentThemedModal
        open={Boolean(stageComplete)}
        title="Congratulations!"
        titleId="yp-stage-complete-title"
        themeData={themeData}
        confirmText="Next"
        closeOnBackdrop={false}
        autoCloseMs={3000}
        onConfirm={handleStageCompleteConfirm}
      >
        <p>{stageComplete?.text}</p>
      </TalentThemedModal>
    </div>
  );
}
